import { toast } from "react-toastify";
import { DRIVER_DETAILS } from "../config/apiEndPoint";
import { GET, MULTIPART_POST } from "config/api.config";

const pickField = (row = {}, ...keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value === null || value === undefined) continue;
        if (typeof value === "string" && value.trim() === "") continue;
        return value;
    }
    return undefined;
};

const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value ?? "")
        .trim()
        .toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
};

/** Parse API value such as `"DL, SSN"` into `["DL", "SSN"]`. */
const parsePendingApprovalDocumentTypes = (raw) => {
    if (raw == null || raw === "") return [];
    if (Array.isArray(raw)) {
        return raw.map((item) => String(item ?? "").trim()).filter(Boolean);
    }
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => String(item ?? "").trim()).filter(Boolean);
            }
        } catch {
            // fall through to comma-separated parsing
        }
        return trimmed
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return [String(raw).trim()].filter(Boolean);
};

/** Normalize GET response (PascalCase or camelCase) for the driver details form. */
export const normalizeDriverDetailsApiRow = (raw = {}) => {
    const row = raw?.data != null && typeof raw.data === "object" && !Array.isArray(raw.data)
        ? raw.data
        : raw;

    if (!row || typeof row !== "object") {
        return null;
    }

    return {
        id: String(pickField(row, "id", "_id") ?? "").trim(),
        requestSource: Number(pickField(row, "RequestSource", "requestSource")) || 0,
        candidateClientId: String(
            pickField(row, "CandidateClientId", "candidateClientId") ?? ""
        ).trim(),
        ssn: String(pickField(row, "SSN", "ssn") ?? "").trim(),
        ssnImage: pickField(row, "SSNImage", "ssnImage"),
        ssnImageUrl: String(
            pickField(row, "ssnImageUrl", "ssnImageLink", "SSNImageUrl", "SSNImageLink") ?? ""
        ).trim(),
        ssnApprovalRequired:
            Number(pickField(row, "SSNApprovalRequired", "ssnApprovalRequired")) || 0,
        showApproveSsn: toBoolean(pickField(row, "showApproveSsn", "ShowApproveSsn")),
        licenseNumber: String(pickField(row, "LicenseNumber", "licenseNumber") ?? "").trim(),
        licenseImage: pickField(row, "LicenseImage", "licenseImage"),
        licenseImageUrl: String(
            pickField(
                row,
                "licenseImageUrl",
                "dlImageLink",
                "LicenseImageUrl",
                "DlImageLink"
            ) ?? ""
        ).trim(),
        licenseClass: String(pickField(row, "LicenseClass", "licenseClass") ?? "").trim(),
        licenseExpiry: pickField(row, "LicenseExpiry", "licenseExpiry"),
        licenseState: String(pickField(row, "LicenseState", "licenseState") ?? "").trim(),
        licenseApprovalRequired:
            Number(pickField(row, "LicenseApprovalRequired", "licenseApprovalRequired")) || 0,
        showApproveDl: toBoolean(pickField(row, "showApproveDl", "ShowApproveDl")),
        medicalCertExpiry: pickField(row, "MedicalCertExpiry", "medicalCertExpiry"),
        medicalCertImage: pickField(row, "MedicalCertImage", "medicalCertImage"),
        medicalCertImageUrl: String(
            pickField(
                row,
                "medicalCertImageUrl",
                "mecImageLink",
                "MedicalCertImageUrl",
                "MecImageLink"
            ) ?? ""
        ).trim(),
        medicalCertApprovalRequired:
            Number(
                pickField(row, "MedicalCertApprovalRequired", "medicalCertApprovalRequired")
            ) || 0,
        showApproveMec: toBoolean(pickField(row, "showApproveMec", "ShowApproveMec")),
        endorsements: String(pickField(row, "Endorsements", "endorsements") ?? "").trim(),
        note: String(pickField(row, "Note", "note") ?? "").trim(),
        updatedBy: Number(pickField(row, "UpdatedBy", "updatedBy")) || 0,
        updatedOn: pickField(row, "updatedOn", "UpdatedOn"),
        approvalRequired: Number(pickField(row, "ApprovalRequired", "approvalRequired")) || 0,
        pendingApprovalDocumentTypes: parsePendingApprovalDocumentTypes(
            pickField(row, "pendingApprovalDocumentTypes", "PendingApprovalDocumentTypes")
        ),
        submitValue: Number(pickField(row, "SubmitValue", "submitValue")) || 0,
    };
};

export const getDriverDetails = async (candidateId = "") => {
    if (!String(candidateId || "").trim()) {
        toast.error("Candidate ID not found");
        return undefined;
    }
    try {
        const response = await GET(DRIVER_DETAILS.BY_CANDIDATE_ID(candidateId));
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};

/**
 * Image id for submit: never send 0. New file => null (file sent separately); else API id or null.
 */
const resolveSubmitImageId = (newFile, storedImageId) => {
    if (newFile) return null;

    if (storedImageId === null || storedImageId === undefined || storedImageId === "") {
        return null;
    }

    if (typeof storedImageId === "number") {
        return Number.isNaN(storedImageId) || storedImageId === 0 ? null : storedImageId;
    }

    const asNumber = Number(storedImageId);
    if (Number.isFinite(asNumber)) {
        return asNumber === 0 ? null : asNumber;
    }

    const asString = String(storedImageId).trim();
    return asString || null;
};

/**
 * Build multipart FormData for driver details update (PascalCase API fields).
 */
export const buildDriverDetailsFormData = ({
    form = {},
    imageIds = {},
    files = {},
    candidateClientId = "",
}) => {
    const formData = new FormData();

    const appendField = (key, value) => {
        if (value === null || value === undefined) {
            formData.append(key, "");
            return;
        }
        formData.append(key, typeof value === "number" ? String(value) : value);
    };

    const ssnImageId = resolveSubmitImageId(
        files.SSNImageFile,
        imageIds.SSNImage
    );
    const licenseImageId = resolveSubmitImageId(
        files.LicenseImageFile,
        imageIds.LicenseImage
    );
    const medicalCertImageId = resolveSubmitImageId(
        files.MedicalCertImageFile,
        imageIds.MedicalCertImage
    );

    appendField("RequestSource", Number(form.requestSource) || 0);
    appendField(
        "CandidateClientId",
        String(candidateClientId || form.candidateClientId || "").trim()
    );
    appendField("SSN", String(form.ssn ?? "").trim());
    appendField("SSNImage", ssnImageId);
    appendField("SSNApprovalRequired", Number(form.ssnApprovalRequired) || 0);
    appendField("LicenseNumber", String(form.licenseNumber ?? "").trim());
    appendField("LicenseImage", licenseImageId);
    appendField("LicenseClass", String(form.licenseClass ?? "").trim());
    appendField("LicenseExpiry", form.licenseExpiry ?? "");
    appendField("LicenseState", String(form.licenseState ?? "").trim());
    appendField("LicenseApprovalRequired", Number(form.licenseApprovalRequired) || 0);
    appendField("MedicalCertExpiry", form.medicalCertExpiry ?? "");
    appendField("MedicalCertImage", medicalCertImageId);
    appendField(
        "MedicalCertApprovalRequired",
        Number(form.medicalCertApprovalRequired) || 0
    );
    appendField("Endorsements", String(form.endorsements ?? "").trim());
    appendField("Note", String(form.note ?? "").trim());
    appendField("UpdatedBy", Number(form.updatedBy) || 0);
    appendField("ApprovalRequired", Number(form.approvalRequired) || 0);
    appendField("SubmitValue", Number(form.submitValue) || 0);

    if (files.SSNImageFile) {
        formData.append("SSNImageFile", files.SSNImageFile);
    }
    if (files.LicenseImageFile) {
        formData.append("LicenseImageFile", files.LicenseImageFile);
    }
    if (files.MedicalCertImageFile) {
        formData.append("MedicalCertImageFile", files.MedicalCertImageFile);
    }

    return formData;
};

/**
 * Update driver details — always multipart `FormData` (same URL as GET).
 */
export const saveDriverDetails = async (candidateId = "", formData) => {
    if (!String(candidateId || "").trim()) {
        toast.error("Candidate ID not found");
        return undefined;
    }
    if (!(formData instanceof FormData)) {
        toast.error("Invalid form payload");
        return undefined;
    }
    try {
        const url = DRIVER_DETAILS.UPDATE_BY_CANDIDATE_ID(candidateId);
        const response = await MULTIPART_POST(url, formData);
        return response;
    } catch (error) {
        toast.error(error?.message);
    }
};
